import React, { useEffect, useContext, useMemo } from 'react';
import loadable from '@loadable/component';
import { Helmet } from 'react-helmet';
import { Switch, useLocation } from 'react-router-dom';
import { renderRoutes } from 'react-router-config';
import CenteredLoader from './components/CenteredLoader';
import NotificationBar from './components/NotificationBar';
import LazyLoad from './components/LazyLoad';
import { Context } from './context/AppContext';
import getRoute from './utils/getRoute';
import config from '../src/client/config';
import Routes from './routes';
import RoutesWithSpeed from './routesWithSpeed';
import { facebookPixel } from './trackingScripts';
import './gtm';
import './styles.css';
import SearchModal from './components/Search/organisms/SearchModal';

const Footer = loadable(() => import('./components/NewFooter'), {
  fallback: <CenteredLoader />,
});

const { mode, title, description } = config;

const hideFooterRoutes = [
  'manage-listing',
  'app-login-error',
  'quote-approval',
  'quote-proposal-order',
  'add-a-business-success',
  'login-loader',
  'listing-post',
];

const App = () => {
  const { userAgent: { isIE } = {} } = useContext(Context) || {};

  const { pathname } = useLocation();

  const route = useMemo(() => getRoute(pathname), [pathname]);

  const notificationParams = isIE
    ? {
        params: {
          show: true,
          message:
            'As Internet Explorer is not secure, we no longer support this browser. These are our recommended browsers to download.',
          duration: 10000,
          buttons: [
            {
              text: 'Download Chrome',
              icon: 'icon-generaldownload',
              href: 'https://www.google.com/chrome/',
              target: '_target',
            },
            {
              text: 'Download Firefox',
              icon: 'icon-generaldownload',
              href: 'https://www.mozilla.org/en-US/firefox/new/',
              target: '_target',
            },
          ],
        },
      }
    : null;

  useEffect(() => {
    if (mode === 'production') {
      facebookPixel.init('1903727653345494');
    }
  }, []);

  const routes = useMemo(
    () =>
      route === 'find' && pathname.includes('-nt') ? RoutesWithSpeed : Routes,
    [pathname]
  );

  return (
    <div id={route}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
      </Helmet>
      <Switch>{renderRoutes(routes)}</Switch>
      {!hideFooterRoutes.includes(route) ? (
        <LazyLoad
          style={{ minHeight: '100px' }}
          className="Footer__Placeholder"
        >
          <Footer />
        </LazyLoad>
      ) : null}
      <NotificationBar {...notificationParams} />
      <SearchModal />
    </div>
  );
};

export default App;
